// mui
import {
  Avatar,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  useTheme,
} from '@mui/material';

// types
import { OrganiseMember } from '@karehero/models';

// components
import { getProfilePictureUrl } from '@/pwa/utils/getProfilePictureUrl';

export type EntryDialogMember = OrganiseMember;

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

export interface AssigneeSelectProps {
  assigneeAccountId: string | null;
  onAssigneeChange: (val: string | null) => void;
  members: EntryDialogMember[];
}

export const AssigneeSelect = ({
  assigneeAccountId,
  onAssigneeChange,
  members,
}: AssigneeSelectProps) => {
  const theme = useTheme();

  const selectedAssignee = members.find(
    (member) => member.id === assigneeAccountId,
  );

  return (
    <FormControl fullWidth>
      <InputLabel shrink>Assignee</InputLabel>
      <Select
        notched
        value={assigneeAccountId ?? ''}
        onChange={(e) => onAssigneeChange(e.target.value || null)}
        label='Assignee'
        renderValue={(selected) => {
          if (!selected) {
            return 'None';
          }

          if (!selectedAssignee) {
            return '';
          }

          return (
            <Stack
              direction='row'
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Avatar
                alt={selectedAssignee.name}
                src={getProfilePictureUrl(selectedAssignee)}
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: 10,
                  ...(!getProfilePictureUrl(selectedAssignee) && {
                    bgcolor: theme.palette.custom.marineGreen[900],
                    color: theme.palette.custom.marineGreen[100],
                  }),
                }}
              >
                {getInitials(selectedAssignee.name)}
              </Avatar>
              <Box component='span'>{selectedAssignee.name}</Box>
            </Stack>
          );
        }}
      >
        <MenuItem value=''>None</MenuItem>
        {members.map((m) => (
          <MenuItem key={m.id} value={m.id}>
            <Stack
              direction='row'
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Avatar
                alt={m.name}
                src={getProfilePictureUrl(m)}
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: 10,
                  ...(!getProfilePictureUrl(m) && {
                    bgcolor: theme.palette.custom.marineGreen[900],
                    color: theme.palette.custom.marineGreen[100],
                  }),
                }}
              >
                {getInitials(m.name)}
              </Avatar>
              <Box component='span'>{m.name}</Box>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

AssigneeSelect.displayName = 'AssigneeSelect';
export default AssigneeSelect;
